import { Money } from '../../components/ui/Money';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, CreditCard, Plane, IdCard, Users, Calendar, Plus, Pencil, Trash2, X, Save, ChevronDown, ChevronUp, Download } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import axios from 'axios';
import useBlinkHighlight from '../../hooks/useBlinkHighlight';

const API = process.env.REACT_APP_BACKEND_URL;

const PortalDocuments = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddDep, setShowAddDep] = useState(false);
  const [editingDep, setEditingDep] = useState(null);
  const [expandedDep, setExpandedDep] = useState(null);
  const navigate = useNavigate();
  const docRefs = useRef({});
  // Reusable scroll-and-blink behavior driven by ?highlight=<slug>
  // (passport / visa / labour-card / emirates-id).
  useBlinkHighlight(docRefs, !loading && !!data);
  const token = localStorage.getItem('portal_token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/api/employee-portal/my-documents`, { headers });
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('portal_token');
        navigate('/employee-portal/login');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [token, navigate]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-600" />
      </div>
    );
  }

  const docs = data?.documents || {};
  const dependents = data?.dependents || [];
  const idDocs = data?.id_documents || {};
  const employeeId = data?.employee_id;

  const formatDate = (d) => {
    if (!d) return '--';
    try {
      return new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return d; }
  };

  const getExpiryStatus = (expiryDate) => {
    if (!expiryDate) return { label: 'Not Set', color: 'bg-gray-100 text-gray-500' };
    const today = new Date();
    const exp = new Date(expiryDate + 'T00:00:00');
    const days = Math.floor((exp - today) / (1000 * 60 * 60 * 24));
    if (days < 0) return { label: 'Expired', color: 'bg-red-100 text-red-700' };
    if (days <= 30) return { label: `${days}d left`, color: 'bg-red-100 text-red-700' };
    if (days <= 90) return { label: `${days}d left`, color: 'bg-amber-100 text-amber-700' };
    return { label: 'Valid', color: 'bg-emerald-100 text-emerald-700' };
  };

  const docCards = [
    { icon: Plane, label: 'Passport', number: docs.passport_number, expiry: docs.passport_expiry, color: 'text-blue-600', bg: 'bg-blue-50' },
    { icon: CreditCard, label: 'Visa', number: docs.visa_number, expiry: docs.visa_expiry, color: 'text-purple-600', bg: 'bg-purple-50', fileKind: 'visa' },
    { icon: IdCard, label: 'Emirates ID', number: docs.emirates_id, expiry: docs.emirates_id_expiry, color: 'text-teal-600', bg: 'bg-teal-50', fileKind: 'emirates_id' },
    { icon: FileText, label: 'Labour Card', number: docs.labour_card_number, expiry: docs.labour_card_expiry, color: 'text-orange-600', bg: 'bg-orange-50' },
  ];

  const handleDeleteDep = async (depId) => {
    if (!window.confirm('Remove this dependent?')) return;
    try {
      await axios.delete(`${API}/api/employee-portal/my-dependents/${depId}`, { headers });
      load();
    } catch (err) { console.error(err); }
  };

  return (
    <div className="space-y-4 max-w-lg mx-auto" data-testid="portal-documents">
      <h2 className="text-lg font-bold text-gray-900">My Documents</h2>

      {docCards.map((doc) => {
        const status = getExpiryStatus(doc.expiry);
        const Icon = doc.icon;
        const slug = doc.label.toLowerCase().replace(/\s/g, '-');
        return (
          <Card
            key={doc.label}
            className="border-0 shadow-sm transition-shadow"
            data-testid={`doc-card-${slug}`}
            ref={(el) => { docRefs.current[slug] = el; }}
          >
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-xl ${doc.bg} flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`h-5 w-5 ${doc.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-gray-900">{doc.label}</p>
                    <Badge className={`text-[10px] ${status.color}`}>{status.label}</Badge>
                  </div>
                  <p className="text-xs text-gray-600 mt-1 font-mono">{doc.number || '--'}</p>
                  <div className="flex items-center gap-1 mt-1.5">
                    <Calendar className="h-3 w-3 text-gray-400" />
                    <p className="text-[11px] text-gray-400">Expires: {formatDate(doc.expiry)}</p>
                  </div>
                  {doc.fileKind && idDocs[doc.fileKind]?.storage_path && (
                    <a
                      href={`${API}/api/employee-documents/${employeeId}/${doc.fileKind}?download=true`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 mt-2 text-[11px] font-medium text-sky-600 hover:text-sky-700 hover:underline"
                      data-testid={`portal-download-${slug}`}
                    >
                      <Download className="h-3 w-3" />
                      Download {idDocs[doc.fileKind].filename ? `(${idDocs[doc.fileKind].filename})` : 'document'}
                    </a>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Additional Documents Section */}
      <AdditionalDocsSection token={token} headers={headers} />

      {/* Dependents Section */}
      <Card className="border-0 shadow-sm" data-testid="dependents-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5" />
              Dependents ({dependents.length})
            </h3>
            <Button size="sm" className="h-7 text-xs bg-sky-600 hover:bg-sky-700 gap-1" onClick={() => { setShowAddDep(true); setEditingDep(null); }} data-testid="add-dependent-btn">
              <Plus className="h-3 w-3" /> Add
            </Button>
          </div>
          {dependents.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">No dependents on file</p>
          ) : (
            <div className="space-y-2">
              {dependents.map((dep, i) => (
                <div key={dep.id || i} className="rounded-lg bg-gray-50 border overflow-hidden" data-testid={`dependent-${dep.id || i}`}>
                  <div className="p-3 flex items-center justify-between">
                    <div className="flex-1" onClick={() => setExpandedDep(expandedDep === dep.id ? null : dep.id)} style={{cursor: 'pointer'}}>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-800">{dep.name}</p>
                        <Badge variant="outline" className="text-[10px]">{dep.relationship}</Badge>
                        {dep.insurance_amount > 0 && (
                          <Badge className="text-[9px] bg-orange-100 text-orange-700"><Money value={dep.insurance_amount} />/yr</Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => { setEditingDep(dep); setShowAddDep(true); }} className="p-1 hover:bg-white rounded" data-testid={`edit-dep-${dep.id}`}>
                        <Pencil className="h-3.5 w-3.5 text-blue-500" />
                      </button>
                      <button onClick={() => handleDeleteDep(dep.id)} className="p-1 hover:bg-white rounded" data-testid={`delete-dep-${dep.id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-red-400" />
                      </button>
                      <button onClick={() => setExpandedDep(expandedDep === dep.id ? null : dep.id)} className="p-1 hover:bg-white rounded">
                        {expandedDep === dep.id ? <ChevronUp className="h-3.5 w-3.5 text-gray-400" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
                      </button>
                    </div>
                  </div>
                  {expandedDep === dep.id && (
                    <div className="px-3 pb-3 pt-1 border-t bg-white space-y-1">
                      {dep.date_of_birth && <DetailRow label="Date of Birth" value={formatDate(dep.date_of_birth)} />}
                      {dep.passport_number && <DetailRow label="Passport" value={`${dep.passport_number}${dep.passport_expiry ? ` (exp: ${formatDate(dep.passport_expiry)})` : ''}`} />}
                      {dep.visa_number && <DetailRow label="Visa" value={`${dep.visa_number}${dep.visa_expiry ? ` (exp: ${formatDate(dep.visa_expiry)})` : ''}`} />}
                      {dep.emirates_id && <DetailRow label="Emirates ID" value={`${dep.emirates_id}${dep.emirates_id_expiry ? ` (exp: ${formatDate(dep.emirates_id_expiry)})` : ''}`} />}
                      {dep.notes && <DetailRow label="Notes" value={dep.notes} />}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      {showAddDep && (
        <DependentDialog
          token={token}
          dep={editingDep}
          onClose={() => { setShowAddDep(false); setEditingDep(null); }}
          onSuccess={() => { setShowAddDep(false); setEditingDep(null); load(); }}
        />
      )}
    </div>
  );
};

const DetailRow = ({ label, value }) => (
  <div className="flex justify-between text-[11px]">
    <span className="text-gray-400">{label}</span>
    <span className="text-gray-700 font-medium">{value}</span>
  </div>
);

/* ── Additional Documents Section ────────── */
const DOC_TYPES = ['Driving License', 'Certificate', 'Insurance Policy', 'Degree/Diploma', 'Training Certificate', 'Medical Report', 'Other'];

const AdditionalDocsSection = ({ token, headers }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/api/employee-portal/my-additional-docs`, { headers });
      setDocs(res.data.documents || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API}/api/employee-portal/my-additional-docs/${id}`, { headers });
      load();
    } catch (err) { console.error(err); }
  };

  const formatDate = (d) => {
    if (!d) return '--';
    try { return new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return d; }
  };

  const getExpiryStatus = (expiryDate) => {
    if (!expiryDate) return null;
    const today = new Date();
    const exp = new Date(expiryDate + 'T00:00:00');
    const days = Math.floor((exp - today) / (1000 * 60 * 60 * 24));
    if (days < 0) return { label: 'Expired', color: 'bg-red-100 text-red-700' };
    if (days <= 30) return { label: `${days}d`, color: 'bg-red-100 text-red-700' };
    if (days <= 90) return { label: `${days}d`, color: 'bg-amber-100 text-amber-700' };
    return { label: 'Valid', color: 'bg-emerald-100 text-emerald-700' };
  };

  return (
    <Card className="border-0 shadow-sm" data-testid="additional-docs-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
            <FileText className="h-3.5 w-3.5" />
            Additional Documents ({docs.length})
          </h3>
          <Button size="sm" className="h-7 text-xs bg-sky-600 hover:bg-sky-700 gap-1" onClick={() => { setShowAdd(true); setEditingDoc(null); }} data-testid="add-additional-doc-btn">
            <Plus className="h-3 w-3" /> Add
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-4"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-sky-600" /></div>
        ) : docs.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-4">No additional documents added</p>
        ) : (
          <div className="space-y-2">
            {docs.map(doc => {
              const expStatus = getExpiryStatus(doc.expiry_date);
              return (
                <div key={doc.id} className="p-3 rounded-lg bg-gray-50 border" data-testid={`addl-doc-${doc.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium text-gray-800">{doc.doc_name}</p>
                        <Badge variant="outline" className="text-[10px]">{doc.doc_type}</Badge>
                        {expStatus && <Badge className={`text-[9px] ${expStatus.color}`}>{expStatus.label}</Badge>}
                      </div>
                      {doc.doc_number && <p className="text-[11px] text-gray-500 mt-0.5 font-mono">{doc.doc_number}</p>}
                      <div className="flex gap-3 mt-1">
                        {doc.issue_date && <span className="text-[10px] text-gray-400">Issued: {formatDate(doc.issue_date)}</span>}
                        {doc.expiry_date && <span className="text-[10px] text-gray-400">Expires: {formatDate(doc.expiry_date)}</span>}
                      </div>
                      {doc.notes && <p className="text-[10px] text-gray-400 mt-0.5">{doc.notes}</p>}
                    </div>
                    <div className="flex gap-1 flex-shrink-0 ml-2">
                      <button onClick={() => { setEditingDoc(doc); setShowAdd(true); }} className="p-1 hover:bg-white rounded">
                        <Pencil className="h-3.5 w-3.5 text-blue-500" />
                      </button>
                      <button onClick={() => handleDelete(doc.id)} className="p-1 hover:bg-white rounded">
                        <Trash2 className="h-3.5 w-3.5 text-red-400" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {showAdd && (
          <AddDocDialog
            token={token}
            doc={editingDoc}
            onClose={() => { setShowAdd(false); setEditingDoc(null); }}
            onSuccess={() => { setShowAdd(false); setEditingDoc(null); load(); }}
          />
        )}
      </CardContent>
    </Card>
  );
};

const AddDocDialog = ({ token, doc, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    doc_type: doc?.doc_type || '',
    doc_name: doc?.doc_name || '',
    doc_number: doc?.doc_number || '',
    issue_date: doc?.issue_date || '',
    expiry_date: doc?.expiry_date || '',
    notes: doc?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isEdit = !!doc?.id;
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.doc_type || !form.doc_name.trim()) { setError('Type and name are required'); return; }
    setSaving(true); setError('');
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (isEdit) {
        await axios.patch(`${API}/api/employee-portal/my-additional-docs/${doc.id}`, form, { headers });
      } else {
        await axios.post(`${API}/api/employee-portal/my-additional-docs`, form, { headers });
      }
      onSuccess();
    } catch (err) { setError(err.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center" data-testid="add-doc-dialog">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto z-10">
        <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <h3 className="text-base font-bold text-gray-900">{isEdit ? 'Edit' : 'Add'} Document</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100"><X className="h-5 w-5 text-gray-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-3">
          <div>
            <Label className="text-xs text-gray-500">Document Type *</Label>
            <select value={form.doc_type} onChange={e => setF('doc_type', e.target.value)}
              className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
              data-testid="addl-doc-type">
              <option value="">Select type</option>
              {DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs text-gray-500">Document Name *</Label>
            <Input value={form.doc_name} onChange={e => setF('doc_name', e.target.value)} placeholder="e.g. UAE Driving License" className="mt-1 h-9 text-sm" data-testid="addl-doc-name" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Document Number</Label>
            <Input value={form.doc_number} onChange={e => setF('doc_number', e.target.value)} className="mt-1 h-9 text-sm" data-testid="addl-doc-number" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-gray-500">Issue Date</Label>
              <Input type="date" value={form.issue_date} onChange={e => setF('issue_date', e.target.value)} className="mt-1 h-9 text-sm" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Expiry Date</Label>
              <Input type="date" value={form.expiry_date} onChange={e => setF('expiry_date', e.target.value)} className="mt-1 h-9 text-sm" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-gray-500">Notes</Label>
            <Input value={form.notes} onChange={e => setF('notes', e.target.value)} className="mt-1 h-9 text-sm" />
          </div>
          {error && <p className="text-sm text-red-500 text-center">{error}</p>}
          <Button type="submit" className="w-full py-5 bg-sky-600 hover:bg-sky-700" disabled={saving || !form.doc_type || !form.doc_name.trim()} data-testid="save-addl-doc-btn">
            {saving ? 'Saving...' : <><Save className="h-4 w-4 mr-2" /> {isEdit ? 'Update' : 'Add'} Document</>}
          </Button>
        </form>
      </div>
    </div>
  );
};


/* ── Add/Edit Dependent Dialog ────────── */
const DependentDialog = ({ token, dep, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    name: dep?.name || '',
    relationship: dep?.relationship || '',
    date_of_birth: dep?.date_of_birth || '',
    passport_number: dep?.passport_number || '',
    passport_expiry: dep?.passport_expiry || '',
    visa_number: dep?.visa_number || '',
    visa_expiry: dep?.visa_expiry || '',
    emirates_id: dep?.emirates_id || '',
    emirates_id_expiry: dep?.emirates_id_expiry || '',
    insurance_amount: dep?.insurance_amount || '',
    notes: dep?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const headers = { Authorization: `Bearer ${token}` };
  const isEdit = !!dep?.id;

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.relationship) { setError('Name and relationship are required'); return; }
    setSaving(true); setError('');
    try {
      const payload = { ...form, insurance_amount: form.insurance_amount ? parseFloat(form.insurance_amount) : null };
      if (isEdit) {
        await axios.patch(`${API}/api/employee-portal/my-dependents/${dep.id}`, payload, { headers });
      } else {
        await axios.post(`${API}/api/employee-portal/my-dependents`, payload, { headers });
      }
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center" data-testid="dependent-dialog">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto z-10">
        <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <h3 className="text-base font-bold text-gray-900">{isEdit ? 'Edit' : 'Add'} Dependent</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100"><X className="h-5 w-5 text-gray-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs text-gray-500">Name *</Label>
              <Input value={form.name} onChange={e => setF('name', e.target.value)} className="mt-1 h-9 text-sm" data-testid="dep-name" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Relationship *</Label>
              <select
                value={form.relationship}
                onChange={e => setF('relationship', e.target.value)}
                className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="dep-relationship"
              >
                <option value="">Select</option>
                <option value="Spouse">Spouse</option>
                <option value="Child">Child</option>
                <option value="Parent">Parent</option>
                <option value="Sibling">Sibling</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Date of Birth</Label>
              <Input type="date" value={form.date_of_birth} onChange={e => setF('date_of_birth', e.target.value)} className="mt-1 h-9 text-sm" data-testid="dep-dob" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-gray-500">Passport No.</Label>
              <Input value={form.passport_number} onChange={e => setF('passport_number', e.target.value)} className="mt-1 h-9 text-sm" data-testid="dep-passport" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Passport Expiry</Label>
              <Input type="date" value={form.passport_expiry} onChange={e => setF('passport_expiry', e.target.value)} className="mt-1 h-9 text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-gray-500">Visa No.</Label>
              <Input value={form.visa_number} onChange={e => setF('visa_number', e.target.value)} className="mt-1 h-9 text-sm" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Visa Expiry</Label>
              <Input type="date" value={form.visa_expiry} onChange={e => setF('visa_expiry', e.target.value)} className="mt-1 h-9 text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-gray-500">Emirates ID</Label>
              <Input value={form.emirates_id} onChange={e => setF('emirates_id', e.target.value)} className="mt-1 h-9 text-sm" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">EID Expiry</Label>
              <Input type="date" value={form.emirates_id_expiry} onChange={e => setF('emirates_id_expiry', e.target.value)} className="mt-1 h-9 text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-gray-500">Insurance (AED/yr)</Label>
              <Input type="number" value={form.insurance_amount} onChange={e => setF('insurance_amount', e.target.value)} className="mt-1 h-9 text-sm" data-testid="dep-insurance" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Notes</Label>
              <Input value={form.notes} onChange={e => setF('notes', e.target.value)} className="mt-1 h-9 text-sm" />
            </div>
          </div>
          {error && <p className="text-sm text-red-500 text-center">{error}</p>}
          <Button type="submit" className="w-full py-5 bg-sky-600 hover:bg-sky-700" disabled={saving || !form.name.trim() || !form.relationship} data-testid="save-dep-btn">
            {saving ? 'Saving...' : <><Save className="h-4 w-4 mr-2" /> {isEdit ? 'Update' : 'Add'} Dependent</>}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default PortalDocuments;
